import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { Permission } from '@holymoly/core';
import { locales, type Locale } from '@holymoly/i18n';
import { PageTitle } from '@/components/PageTitle';
import { Field } from '@/components/ui/Field';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toaster';
import { authClient } from '@/lib/auth-client';
import { errorText } from '@/lib/errors';
import { formatDate, formatDateTime } from '@/lib/format';
import { setLocale } from '@/lib/i18n';
import { client, orpc } from '@/lib/orpc';
import { meQuery, useMe } from '@/lib/session';

const tabs = ['profile', 'security', 'tokens'] as const;
type Tab = (typeof tabs)[number];

export const Route = createFileRoute('/_app/account')({
  validateSearch: z.object({ tab: z.enum(tabs).optional() }),
  component: AccountPage
});

function AccountPage() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const tab: Tab = me.twoFactorSetupRequired ? 'security' : (search.tab ?? 'profile');
  const labels: Record<Tab, string> = { profile: t('account.tabProfile'), security: t('account.tabSecurity'), tokens: t('account.tabTokens') };

  return (
    <div className="space-y-6 p-6">
      <PageTitle title={t('account.title')} description={me.user.email} />
      {me.twoFactorSetupRequired && (
        <div role="alert" className="alert alert-warning alert-soft">
          <span className="iconify lucide--shield-alert size-5" />
          <span>{t('account.twoFactorRequired')}</span>
        </div>
      )}
      <div role="tablist" className="tabs tabs-border">
        {tabs.map((key) => (
          <button
            key={key}
            role="tab"
            type="button"
            className={`tab ${tab === key ? 'tab-active' : ''}`}
            aria-selected={tab === key}
            disabled={me.twoFactorSetupRequired && key !== 'security'}
            onClick={() => void navigate({ to: '/account', search: { tab: key }, replace: true })}>
            {labels[key]}
          </button>
        ))}
      </div>
      {tab === 'profile' && <ProfileTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'tokens' && <TokensTab />}
    </div>
  );
}

function useRefreshMe() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: meQuery().queryKey });
}

function ProfileTab() {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const refresh = useRefreshMe();
  const [name, setName] = useState(me.user.name);
  const save = useMutation({
    mutationFn: () => client.me.updateProfile({ name }),
    onSuccess: async () => {
      await refresh();
      toast('success', t('account.saved'));
    },
    onError: (e) => toast('error', errorText(t, e))
  });
  const changeLocale = async (locale: Locale) => {
    setLocale(locale);
    await client.me.updatePreferences({ locale });
    await refresh();
  };

  return (
    <div className="card bg-base-100 max-w-xl shadow-sm">
      <div className="card-body">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}>
          <Field label={t('account.name')}>
            {(id) => <input id={id} className="input w-full" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />}
          </Field>
          <Field label={t('account.language')}>
            {(id) => (
              <select id={id} className="select w-full" value={me.locale} onChange={(e) => void changeLocale(e.target.value as Locale)}>
                {locales.map((l) => (
                  <option key={l} value={l}>
                    {t(`languages.${l}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <button type="submit" className="btn btn-primary mt-4" disabled={save.isPending || name.trim() === me.user.name}>
            {t('account.save')}
          </button>
        </form>
      </div>
    </div>
  );
}

function SecurityTab() {
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <TwoFactorCard />
      <PasswordCard />
      <PasskeysCard />
    </div>
  );
}

function PasswordCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next.length < 12) return setError(t('auth.passwordTooShort'));
    if (next !== confirm) return setError(t('auth.passwordsDiffer'));
    setBusy(true);
    const { error: err } = await authClient.changePassword({ currentPassword: current, newPassword: next, revokeOtherSessions: true });
    setBusy(false);
    if (err) return setError(err.status === 429 ? t('auth.tooManyAttempts') : t('auth.invalidCredentials'));
    setError(null);
    setCurrent('');
    setNext('');
    setConfirm('');
    toast('success', t('account.passwordChanged'));
  };

  return (
    <div className="card bg-base-100 shadow-sm">
      <form className="card-body" onSubmit={submit}>
        <h2 className="card-title text-base">{t('account.changePassword')}</h2>
        <input type="text" className="hidden" autoComplete="username" readOnly />
        <PasswordInput label={t('account.currentPassword')} value={current} onChange={setCurrent} autoComplete="current-password" />
        <PasswordInput label={t('auth.newPassword')} value={next} onChange={setNext} autoComplete="new-password" hint={t('auth.passwordHint')} />
        <PasswordInput label={t('auth.confirmPassword')} value={confirm} onChange={setConfirm} autoComplete="new-password" error={error} />
        <div>
          <button type="submit" className="btn btn-primary mt-4" disabled={busy}>
            {busy && <span className="loading loading-spinner loading-sm" />}
            {t('auth.savePassword')}
          </button>
        </div>
      </form>
    </div>
  );
}

/** QR code of the otpauth:// URI, rendered locally (the secret never leaves the browser). */
function TotpQrCode({ uri, label }: { uri: string; label: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void QRCode.toDataURL(uri, { errorCorrectionLevel: 'M', margin: 1, width: 200 }).then((url) => alive && setSrc(url));
    return () => {
      alive = false;
    };
  }, [uri]);
  return src ? <img src={src} alt={label} width={200} height={200} className="rounded-box bg-white p-2" /> : <div className="skeleton size-[200px]" />;
}

/** Splits the base32 secret of an otpauth:// URI into readable groups of four. */
const secretFromUri = (uri: string) => (new URL(uri).searchParams.get('secret') ?? '').replace(/(.{4})/g, '$1 ').trim();

function TwoFactorCard() {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const refresh = useRefreshMe();
  const [password, setPassword] = useState('');
  const [setup, setSetup] = useState<{ totpURI: string; backupCodes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [copied, setCopied] = useState(false);
  const enabled = me.user.twoFactorEnabled;
  const required = me.twoFactorRequired;

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { data, error: err } = await authClient.twoFactor.enable({ password });
    setBusy(false);
    if (err || !data || data.method !== 'totp') return setError(t('auth.invalidCredentials'));
    setError(null);
    setPassword('');
    setSetup({ totpURI: data.totpURI, backupCodes: data.backupCodes });
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { error: err } = await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, '') });
    setBusy(false);
    if (err) return setError(t('auth.invalidCode'));
    setSetup(null);
    setCode('');
    setError(null);
    await refresh();
    toast('success', t('account.twoFactorEnabled'));
  };

  const disable = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { error: err } = await authClient.twoFactor.disable({ password });
    setBusy(false);
    if (err) return setError(t('auth.invalidCredentials'));
    setDisabling(false);
    setPassword('');
    await refresh();
    toast('success', t('account.twoFactorDisabled'));
  };

  const copySecret = async () => {
    await navigator.clipboard.writeText(secretFromUri(setup!.totpURI).replace(/\s/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="card bg-base-100 shadow-sm xl:col-span-2">
      <div className="card-body">
        <div className="flex items-center justify-between gap-3">
          <h2 className="card-title text-base">{t('account.twoFactor')}</h2>
          <span className={`badge badge-soft ${enabled ? 'badge-success' : 'badge-warning'}`}>
            {enabled ? t('account.twoFactorOn') : t('account.twoFactorOff')}
          </span>
        </div>

        {!enabled && !setup && (
          <form onSubmit={start} className="mt-2 max-w-md">
            <p className="text-base-content/70 text-sm">{t('account.confirmWithPassword')}</p>
            <PasswordInput label={t('auth.password')} value={password} onChange={setPassword} autoComplete="current-password" error={error} />
            <button type="submit" className="btn btn-primary mt-3" disabled={busy}>
              {t('account.enableTwoFactor')}
            </button>
          </form>
        )}

        {setup && (
          <div className="mt-2 grid gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <p className="text-sm">{t('account.twoFactorScan')}</p>
              <TotpQrCode uri={setup.totpURI} label={t('account.twoFactorQrAlt')} />
              <p className="text-base-content/70 text-sm">{t('account.twoFactorStep1')}</p>
              <div className="bg-base-200 rounded-box flex items-center justify-between gap-2 p-3">
                <code className="font-mono text-sm break-all" aria-label={t('account.secretKey')}>
                  {secretFromUri(setup.totpURI)}
                </code>
                <button type="button" className="btn btn-sm btn-ghost" onClick={copySecret}>
                  <span className={`iconify size-4 ${copied ? 'lucide--check' : 'lucide--copy'}`} />
                  {copied ? t('account.copied') : t('account.copy')}
                </button>
              </div>
              <a href={setup.totpURI} className="link link-primary text-sm">
                {t('account.twoFactorOpenApp')}
              </a>
              <form onSubmit={verify}>
                <p className="mt-3 text-sm">{t('account.twoFactorStep2')}</p>
                <div className="mt-2 flex gap-2">
                  <input
                    className={`input w-40 font-mono tracking-widest ${error ? 'input-error' : ''}`}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    aria-label={t('auth.code')}
                    required
                  />
                  <button type="submit" className="btn btn-primary" disabled={busy}>
                    {t('auth.verify')}
                  </button>
                </div>
                {error && <p className="text-error mt-2 text-sm">{error}</p>}
              </form>
            </div>
            <div>
              <p className="font-medium">{t('account.backupCodes')}</p>
              <p className="text-base-content/70 mt-1 text-sm">{t('account.backupCodesLead')}</p>
              <ul className="bg-base-200 rounded-box mt-3 grid grid-cols-2 gap-1 p-3 font-mono text-sm">
                {setup.backupCodes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {enabled && !disabling && (
          <div className="mt-2">
            {required ? (
              <p className="text-base-content/70 text-sm">{t('account.cannotDisableRequired')}</p>
            ) : (
              <button type="button" className="btn btn-outline btn-error btn-sm" onClick={() => setDisabling(true)}>
                {t('account.disableTwoFactor')}
              </button>
            )}
          </div>
        )}
        {enabled && disabling && (
          <form onSubmit={disable} className="mt-2 max-w-md">
            <p className="text-base-content/70 text-sm">{t('account.confirmWithPassword')}</p>
            <PasswordInput label={t('auth.password')} value={password} onChange={setPassword} autoComplete="current-password" error={error} />
            <div className="mt-3 flex gap-2">
              <button type="submit" className="btn btn-error btn-sm" disabled={busy}>
                {t('account.disableTwoFactor')}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDisabling(false)}>
                {t('admin.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function PasskeysCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const passkeys = useQuery({
    queryKey: ['passkeys'],
    queryFn: async () => (await authClient.passkey.listUserPasskeys()).data ?? []
  });
  const add = async (e: FormEvent) => {
    e.preventDefault();
    const res = await authClient.passkey.addPasskey({ name: name.trim() || undefined });
    if (res?.error) return toast('error', t('auth.passkeyFailed'));
    setName('');
    await passkeys.refetch();
  };
  const remove = async (id: string) => {
    await authClient.passkey.deletePasskey({ id });
    await passkeys.refetch();
  };

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h2 className="card-title text-base">{t('account.passkeys')}</h2>
        <p className="text-base-content/70 text-sm">{t('account.passkeysLead')}</p>
        <ul className="mt-2 space-y-2">
          {passkeys.data?.length === 0 && <li className="text-base-content/60 text-sm">{t('account.noPasskeys')}</li>}
          {passkeys.data?.map((pk) => (
            <li key={pk.id} className="border-base-300 rounded-box flex items-center justify-between gap-2 border px-3 py-2 text-sm">
              <span className="flex items-center gap-2">
                <span className="iconify lucide--fingerprint size-4" />
                {pk.name || '–'}
                <span className="text-base-content/50">{formatDate(pk.createdAt)}</span>
              </span>
              <button type="button" className="btn btn-ghost btn-xs" onClick={() => void remove(pk.id)}>
                {t('account.remove')}
              </button>
            </li>
          ))}
        </ul>
        <form onSubmit={add} className="mt-3 flex gap-2">
          <input className="input input-sm grow" placeholder={t('account.passkeyName')} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
          <button type="submit" className="btn btn-sm btn-outline border-base-300">
            <span className="iconify lucide--plus size-4" />
            {t('account.addPasskey')}
          </button>
        </form>
      </div>
    </div>
  );
}

const EXPIRY_OPTIONS = [7, 30, 90, 365] as const;

function TokensTab() {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const tokens = useQuery(orpc.apiTokens.list.queryOptions());
  const canCreate = me.permissions.includes('api_tokens.create');
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<Permission[]>([]);
  const [days, setDays] = useState<number>(30);
  const [secret, setSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => client.apiTokens.create({ name, scope, expiresInDays: days }),
    onSuccess: async (res) => {
      setSecret(res.secret);
      setName('');
      setScope([]);
      await tokens.refetch();
    },
    onError: (e) => toast('error', errorText(t, e))
  });
  const revoke = useMutation({
    mutationFn: (id: string) => client.apiTokens.revoke({ id }),
    onSuccess: async () => {
      setRevoking(null);
      await tokens.refetch();
    },
    onError: (e) => toast('error', errorText(t, e))
  });

  const status = (tok: { revokedAt: Date | null; expiresAt: Date }) =>
    tok.revokedAt ? t('account.revoked') : new Date(tok.expiresAt) <= new Date() ? t('account.expired') : null;

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-base-content/70 max-w-2xl text-sm">{t('account.tokensLead')}</p>
          {canCreate ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>
              <span className="iconify lucide--plus size-4" />
              {t('account.createToken')}
            </button>
          ) : (
            <span className="text-base-content/60 text-sm">{t('account.noPermissionForTokens')}</span>
          )}
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('account.tokenName')}</th>
                <th>{t('account.tokenScope')}</th>
                <th>{t('account.lastUsed')}</th>
                <th>{t('account.expiresAt')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tokens.data?.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-base-content/60">
                    {t('account.noTokens')}
                  </td>
                </tr>
              )}
              {tokens.data?.map((tok) => (
                <tr key={tok.id} className={status(tok) ? 'opacity-60' : ''}>
                  <td>
                    <p className="font-medium">{tok.name}</p>
                    <p className="text-base-content/50 font-mono text-xs">{tok.prefix}…</p>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {tok.scope.map((p) => (
                        <span key={p} className="badge badge-ghost badge-sm" title={t(`permissions.${p}`)}>
                          {p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-sm">{tok.lastUsedAt ? formatDateTime(tok.lastUsedAt) : t('account.never')}</td>
                  <td className="text-sm">{status(tok) ?? formatDate(tok.expiresAt)}</td>
                  <td className="text-end">
                    {!status(tok) && (
                      <button type="button" className="btn btn-ghost btn-xs text-error" onClick={() => setRevoking(tok.id)}>
                        {t('account.revoke')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setSecret(null);
        }}
        title={t('account.createToken')}>
        {secret ? (
          <div className="space-y-3">
            <p className="text-sm">{t('account.tokenCreated')}</p>
            <code className="bg-base-200 rounded-box block p-3 font-mono text-sm break-all">{secret}</code>
            <button type="button" className="btn btn-sm" onClick={() => void navigator.clipboard.writeText(secret)}>
              <span className="iconify lucide--copy size-4" />
              {t('account.copy')}
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}>
            <Field label={t('account.tokenName')}>
              {(id) => <input id={id} className="input w-full" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required autoFocus />}
            </Field>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">{t('account.tokenScope')}</legend>
              <div className="grid gap-1">
                {me.permissions.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={scope.includes(p)}
                      onChange={(e) => setScope(e.target.checked ? [...scope, p] : scope.filter((x) => x !== p))}
                    />
                    {t(`permissions.${p}`)}
                  </label>
                ))}
              </div>
            </fieldset>
            <Field label={t('account.tokenExpires')}>
              {(id) => (
                <select id={id} className="select w-full" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                  {EXPIRY_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {t('account.days', { count: d })}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <button type="submit" className="btn btn-primary mt-4 w-full" disabled={create.isPending || scope.length === 0}>
              {t('account.createToken')}
            </button>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={revoking !== null}
        text={t('account.revoke')}
        danger
        busy={revoke.isPending}
        onClose={() => setRevoking(null)}
        onConfirm={() => revoke.mutate(revoking!)}
      />
    </div>
  );
}
